import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { UserPlus, Search, Loader2 } from "lucide-react";

export default function Patients() {
  const navigate = useNavigate();
  const [patients, setPatients] = useState([]);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ name: "", sex: "", age: "", history: "", phone: "" });

  const load = () => api.get("/patients").then((r) => setPatients(r.data));
  useEffect(() => { load(); }, []);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const create = async () => {
    if (!form.name.trim()) { toast.error("Informe o nome do paciente"); return; }
    setSaving(true);
    try {
      const payload = { ...form, age: form.age ? parseInt(form.age) : null };
      const { data } = await api.post("/patients", payload);
      toast.success("Paciente cadastrado");
      setOpen(false);
      setForm({ name: "", sex: "", age: "", history: "", phone: "" });
      navigate(`/pacientes/${data.id}`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const filtered = patients.filter((p) => p.name.toLowerCase().includes(q.toLowerCase()));

  return (
    <div className="p-6 sm:p-10 max-w-7xl">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-8">
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-ai mb-2">Gestão</p>
          <h1 className="font-heading text-4xl font-light tracking-tighter">Pacientes</h1>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="add-patient-btn"><UserPlus className="w-4 h-4 mr-2" /> Novo paciente</Button>
          </DialogTrigger>
          <DialogContent data-testid="patient-dialog">
            <DialogHeader><DialogTitle>Cadastrar paciente</DialogTitle></DialogHeader>
            <div className="space-y-4 py-2">
              <div className="space-y-2">
                <Label>Nome completo</Label>
                <Input value={form.name} onChange={set("name")} data-testid="patient-name-input" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Sexo</Label>
                  <Select value={form.sex} onValueChange={(v) => setForm({ ...form, sex: v })}>
                    <SelectTrigger data-testid="patient-sex-select"><SelectValue placeholder="Selecione" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Feminino">Feminino</SelectItem>
                      <SelectItem value="Masculino">Masculino</SelectItem>
                      <SelectItem value="Outro">Outro</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Idade</Label>
                  <Input type="number" value={form.age} onChange={set("age")} data-testid="patient-age-input" />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Telefone</Label>
                <Input value={form.phone} onChange={set("phone")} data-testid="patient-phone-input" />
              </div>
              <div className="space-y-2">
                <Label>Histórico clínico</Label>
                <Textarea value={form.history} onChange={set("history")} rows={3} data-testid="patient-history-input"
                  placeholder="Doenças, medicamentos, histórico familiar..." />
              </div>
            </div>
            <DialogFooter>
              <Button onClick={create} disabled={saving} data-testid="save-patient-btn">
                {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} Salvar
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="relative mb-6 max-w-sm">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <Input placeholder="Buscar paciente..." value={q} onChange={(e) => setQ(e.target.value)}
          className="pl-9" data-testid="patient-search" />
      </div>

      {filtered.length === 0 ? (
        <Card className="p-10 text-center text-muted-foreground border-dashed">Nenhum paciente encontrado.</Card>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map((p) => (
            <Card key={p.id} data-testid={`patient-row-${p.id}`}
              onClick={() => navigate(`/pacientes/${p.id}`)}
              className="p-5 border-border shadow-none cursor-pointer hover:-translate-y-[2px] hover:border-primary/40 transition-transform duration-200">
              <div className="flex items-center gap-3">
                <div className="w-11 h-11 rounded-full bg-primary/10 text-primary flex items-center justify-center font-heading font-bold">
                  {p.name.slice(0, 2).toUpperCase()}
                </div>
                <div className="min-w-0">
                  <p className="font-medium truncate">{p.name}</p>
                  <p className="text-xs text-muted-foreground">{p.sex || "—"} · {p.age ? `${p.age} anos` : "idade n/d"}</p>
                </div>
              </div>
              <div className="flex gap-4 mt-4 text-xs text-muted-foreground">
                <span>{p.lab_count} exames lab</span>
                <span>{p.imaging_count} imagens</span>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
